import { filtersToQuery } from '@ecom/contracts';
import { ROUTES } from './generated/routes';
/**
 * The back-office surface (`/api/admin/*`).
 *
 * Kept apart from the storefront surface so the split is visible in code: these
 * calls all require a staff or owner session and every one of them is audited
 * server-side (ADR-011).
 */
function route(name, params = {}) {
    return ROUTES[name].path.replace(/\{(\w+)\}/g, (_match, key) => {
        const value = params[key];
        if (value === undefined)
            throw new Error(`Route ${name} is missing the "${key}" parameter.`);
        return encodeURIComponent(value);
    });
}
function query(input) {
    if (!input)
        return undefined;
    const output = {};
    for (const [key, value] of Object.entries(input)) {
        if (value !== undefined && value !== null && value !== '') {
            output[key] = value;
        }
    }
    return output;
}
export class AdminApi {
    client;
    constructor(client) {
        this.client = client;
    }
    orders = {
        list: (params) => this.client.requestList('GET', route('orders_adminOrders'), {
            query: query(params),
        }),
        get: (id) => this.client.request('GET', route('orders_adminOrder', { id })),
        /**
         * Moves an order along its state machine. The API refuses illegal
         * transitions, so the UI offers only the moves it believes are legal and
         * still surfaces the API's refusal if it is wrong.
         */
        updateStatus: (id, body) => this.client.request('PATCH', route('orders_updateStatus', { id }), { body }),
        /** Money back without a return (ADR-061). Owner only; a cash order needs `settlement`. */
        refund: (id, body) => this.client.request('POST', route('orders_refund', { id }), { body }),
        /** The carriers the ship dialog offers, with their tracking-link templates. */
        carriers: () => this.client.requestList('GET', route('orders_carriers')),
        // ─── Orders staff place and change (ADR-062) ─────────────────────────────
        /** Variants on sale, searched by SKU, barcode or product name. */
        sellableVariants: (params) => this.client.requestList('GET', route('orders_sellableVariants'), { query: query(params) }),
        /** What an order would cost, for the customer it is for; nothing is claimed. */
        quote: (body) => this.client.request('POST', route('orders_quoteStaffOrder'), { body }),
        /** Places it through the shopper's checkout. */
        create: (body) => this.client.request('POST', route('orders_createStaffOrder'), { body }),
        /** Contact, the customer's note, billing — never money; the address is `updateAddress`. */
        updateDetails: (id, body) => this.client.request('PATCH', route('orders_updateDetails', { id }), { body }),
        /** What a change to the items would cost; nothing changes. */
        previewItems: (id, body) => this.client.request('POST', route('orders_previewItems', { id }), { body }),
        /** Changes a cash order's items and delivery before it ships. */
        updateItems: (id, body) => this.client.request('PUT', route('orders_updateItems', { id }), { body }),
        /** How many orders sit in each status — tab counts and the menu badge (ADR-059). */
        counts: () => this.client.request('GET', route('orders_adminOrderCounts')),
        /** The list's own filter as a CSV, built on the server — never assembled from pages. */
        export: (params) => this.client.download(route('orders_adminOrdersExport'), {
            query: query(params),
        }),
        /** The 4×6 shipping label for one order (ADR-068). */
        label: (orderId) => this.client.download(route('orders_adminLabel', { id: orderId })),
        /** Shipping labels for a selection, one a page, in the order given — at most 50. */
        labels: (orderIds) => this.client.download(route('orders_adminLabels'), { query: { ids: orderIds.join(',') } }),
        /** A note for staff on the timeline; never shown to the customer (ADR-059). */
        addNote: (id, body) => this.client.request('POST', route('orders_addNote', { id }), { body }),
        /** Give the order to someone on the staff, or to nobody (ADR-074). */
        assign: (id, assigneeId) => this.client.request('PATCH', route('orders_assign', { id }), { body: { assigneeId } }),
        /** "Deliver now": any line still waiting handed over, and the downloads mailed again (ADR-077). */
        deliverDigital: (id) => this.client.request('POST', route('orders_deliverDigital', { id })),
        /** Sends a customer message again — only one still true of the order. */
        resendNotification: (id, body) => this.client.request('POST', route('orders_resendNotification', { id }), { body }),
        /** Corrects the street, contact and directions before the order leaves; the city stays. */
        updateAddress: (id, body) => this.client.request('PATCH', route('orders_updateAddress', { id }), { body }),
    };
    customers = {
        list: (params) => this.client.requestList('GET', route('admin_listCustomers'), {
            query: query(params),
        }),
        /** How many customers stand in each stage now (ADR-103). */
        stages: () => this.client.requestList('GET', route('admin_customerStages')),
        /** At risk now, the biggest spenders first, nobody with an open follow-up (ADR-104). */
        toCall: (params) => this.client.requestList('GET', route('admin_customersToCall'), { query: query(params) }),
        /** The tags in use, with how many customers carry each (ADR-064). */
        tags: () => this.client.requestList('GET', route('admin_customerTags')),
        /** The list's own filter as a CSV, built on the server — never assembled from pages. */
        export: (params) => this.client.download(route('admin_exportCustomers'), { query: query(params) }),
        /** Orders, addresses, returns, reviews and staff notes in one call. */
        get: (id) => this.client.request('GET', route('admin_customer', { id })),
        /** Notes are appended and attributed, never edited. */
        addNote: (id, body) => this.client.request('POST', route('admin_addNote', { id }), {
            body: { body },
        }),
        /** Correct a name or number; blocking (`isActive: false`) is the owner's. */
        update: (id, body) => this.client.request('PATCH', route('admin_updateCustomer', { id }), { body }),
        /** People who bought without an account, one row per email. */
        guests: (params) => this.client.requestList('GET', route('admin_guests'), { query: query(params) }),
        /** Everything held about a customer, for a request made to the store (R10, owner only). */
        exportData: (id) => this.client.request('GET', route('privacy_exportCustomer', { id })),
        /** Erases a customer on their request: the books keep the orders, stripped of the person (R10). */
        erase: (id) => this.client.request('POST', route('privacy_eraseCustomer', { id })),
    };
    /** Trade price lists (ADR-045): a percentage off retail, and exact prices per variant. */
    priceLists = {
        list: (params) => this.client.requestList('GET', route('tradeAdmin_lists'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('tradeAdmin_one', { id })),
        create: (body) => this.client.request('POST', route('tradeAdmin_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('tradeAdmin_update', { id }), { body }),
        /** Refused while an account buys from it (`PRICE_LIST_IN_USE`). */
        remove: (id) => this.client.request('DELETE', route('tradeAdmin_remove', { id })),
        /** Every variant as this list prices it: retail, the list price, and any exact price. */
        variants: (id, params) => this.client.requestList('GET', route('tradeAdmin_variants', { id }), { query: query(params) }),
        /** Halalas; beats the list's percentage for this variant. */
        setItem: (id, variantId, price) => this.client.request('PUT', route('tradeAdmin_setItem', { id, variantId }), { body: { price } }),
        removeItem: (id, variantId) => this.client.request('DELETE', route('tradeAdmin_removeItem', { id, variantId })),
    };
    /** A customer's trade account (ADR-045). */
    trade = {
        account: (customerId) => this.client.request('GET', route('tradeAdmin_account', { id: customerId })),
        /** Makes a customer a trade account, or changes one — its company, its list, its suspension. */
        saveAccount: (customerId, body) => this.client.request('PUT', route('tradeAdmin_saveAccount', { id: customerId }), { body }),
    };
    /** Looks (R17): products that go together, each with how much. */
    looks = {
        list: (params) => this.client.requestList('GET', route('looks_list'), { query: query(params) }),
        get: (id, locale) => this.client.request('GET', route('looks_get', { id }), { query: query({ locale }) }),
        create: (body, locale) => this.client.request('POST', route('looks_create'), { body, query: query({ locale }) }),
        /** Fields as sent; `items`, when sent, replaces the list in its new order. */
        update: (id, body, locale) => this.client.request('PATCH', route('looks_update', { id }), { body, query: query({ locale }) }),
        remove: (id) => this.client.request('DELETE', route('looks_remove', { id })),
    };
    /**
     * The options library (ADR-052): colour, "sold as", size — named once for
     * the store; every product's variants pick their values from it.
     */
    options = {
        list: () => this.client.requestList('GET', route('options_list')),
        create: (body) => this.client.request('POST', route('options_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('options_update', { id }), { body }),
        /** Searched on the server and paged, like every picker's source. */
        values: (optionId, params) => this.client.requestList('GET', route('options_values', { id: optionId }), {
            query: query(params),
        }),
        createValue: (optionId, body) => this.client.request('POST', route('options_createValue', { id: optionId }), { body }),
        /** Reaches every product using the value, except where one calls it something of its own. */
        updateValue: (valueId, body) => this.client.request('PATCH', route('options_updateValue', { valueId }), { body }),
    };
    /** Businesses asking for a trade account, and the store's answer (ADR-046). */
    tradeApplications = {
        list: (params) => this.client.requestList('GET', route('tradeApplications_list'), { query: query(params) }),
        /** With its documents behind ten-minute links: read it when opened, not before. */
        get: (id, locale) => this.client.request('GET', route('tradeApplications_get', { id }), { query: query({ locale }) }),
        /** Onto a price list: the customer's trade account is made from the application. */
        approve: (id, body, locale) => this.client.request('POST', route('tradeApplications_approve', { id }), {
            body,
            query: query({ locale }),
        }),
        requestChanges: (id, note, locale) => this.client.request('POST', route('tradeApplications_requestChanges', { id }), {
            body: { note },
            query: query({ locale }),
        }),
        reject: (id, note, locale) => this.client.request('POST', route('tradeApplications_reject', { id }), {
            body: { note },
            query: query({ locale }),
        }),
    };
    discounts = {
        /** `activeOnly`: switched on and inside their dates. Send `true` or leave it out — the API reads any value as true. */
        list: (params) => this.client.requestList('GET', route('adminDiscounts_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('adminDiscounts_get', { id })),
        create: (body) => this.client.request('POST', route('adminDiscounts_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('adminDiscounts_update', { id }), { body }),
        /** Codes are switched off, never deleted — they may already be printed. */
        disable: (id) => this.client.request('PATCH', route('adminDiscounts_disable', { id })),
        /** What a coupon did: orders sold, refunded and abandoned, customers, discount and revenue (ADR-063). */
        usage: (id) => this.client.request('GET', route('couponUsage_couponUsage', { id })),
        /** The orders that used a coupon, newest first. */
        orders: (id, params) => this.client.requestList('GET', route('couponUsage_couponOrders', { id }), { query: query(params) }),
    };
    /** What shoppers are holding (ADR-063): carts, wishlists and compare lists across the store. */
    shopperActivity = {
        carts: (params) => this.client.requestList('GET', route('shopperActivity_carts'), { query: query(params) }),
        cartSummary: () => this.client.request('GET', route('shopperActivity_cartSummary')),
        /** The abandoned-cart message for one account's cart, by mail or text — once a day at most (ADR-066). */
        remind: (cartId, body) => this.client.request('POST', route('cartReminder_remind', { id: cartId }), { body }),
        /** Products on one kind of saved list, most saved first. */
        savedProducts: (params) => this.client.requestList('GET', route('shopperActivity_savedProducts'), {
            query: query(params),
        }),
        /** The accounts that saved a product; guests are counted on its row, never named. */
        savers: (productId, params) => this.client.requestList('GET', route('shopperActivity_savers', { productId }), {
            query: query(params),
        }),
    };
    catalog = {
        /** Brands are a short list, returned whole — the product form uses a select. */
        /** Searched and paged like every other list — a reseller may have hundreds. */
        brands: (params) => this.client.requestList('GET', route('adminCatalog_brands'), { query: query(params) }),
        /** Names the brand a form already holds, which a search need not include. */
        brand: (id, locale) => this.client.request('GET', route('adminCatalog_getBrand', { id }), {
            query: query({ locale }),
        }),
        /** Names, description, logo, search result; a new slug leaves the old link redirecting (ADR-095). */
        updateBrand: (id, body) => this.client.request('PATCH', route('adminCatalog_updateBrand', { id }), { body }),
        createBrand: (body) => this.client.request('POST', route('adminCatalog_createBrand'), { body }),
        products: (params) => this.client.requestList('GET', route('adminCatalog_listProducts'), {
            query: query(params),
        }),
        product: (id) => this.client.request('GET', route('adminCatalog_getProduct', { id })),
        /** The product grid (ADR-090): rows are variants; `limit`, the cursor and `total` count products. */
        grid: (params) => this.client.requestList('GET', route('adminCatalog_grid'), { query: query(params) }),
        /** Saves the changed cells; rows refused come back with their reason, the rest are saved. */
        saveGrid: (body) => this.client.request('PATCH', route('adminCatalog_saveGrid'), { body }),
        createProduct: (body) => this.client.request('POST', route('adminCatalog_createProduct'), { body }),
        updateProduct: (id, body) => this.client.request('PATCH', route('adminCatalog_updateProduct', { id }), { body }),
        /** Attaches an uploaded film (R4). */
        addVideo: (productId, body) => this.client.request('POST', route('adminCatalog_addVideo', { id: productId }), { body }),
        removeVideo: (videoId) => this.client.request('DELETE', route('adminCatalog_removeVideo', { videoId })),
        /** The products listed directly in a category, in its hand-picked order (R3). */
        categoryProducts: (id) => this.client
            .request('GET', route('adminCatalog_categoryProducts', { id })),
        /** A category's "featured" order: every listed product, each once (R3). */
        setCategoryOrder: (id, productIds) => this.client.request('PUT', route('adminCatalog_setCategoryOrder', { id }), {
            body: { productIds },
        }),
        /** Labels, swatches and order for a product's option values; replaces the list (R1). */
        setOptionValues: (id, values) => this.client.request('PUT', route('adminCatalog_setOptionValues', { id }), {
            body: { values },
        }),
        /** A draft copy — fields, variants, photos, specifications — SKUs suffixed -COPY, stock at nothing. */
        duplicateProduct: (id) => this.client.request('POST', route('adminCatalog_duplicateProduct', { id })),
        archiveProduct: (id) => this.client.request('DELETE', route('adminCatalog_archiveProduct', { id })),
        categories: (params) => this.client.requestList('GET', route('adminCatalog_listCategories'), {
            query: query(params),
        }),
        category: (id, locale) => this.client.request('GET', route('adminCatalog_getCategory', { id }), {
            query: query({ locale }),
        }),
        categoryTree: () => this.client.request('GET', route('adminCatalog_categoryTree')),
        createCategory: (body) => this.client.request('POST', route('adminCatalog_createCategory'), { body }),
        updateCategory: (id, body) => this.client.request('PATCH', route('adminCatalog_updateCategory', { id }), { body }),
        /** Deactivated, never deleted — products and orders point at it. */
        deactivateCategory: (id) => this.client.request('DELETE', route('adminCatalog_deactivateCategory', { id })),
        addVariant: (productId, body) => this.client.request('POST', route('adminCatalog_addVariant', { id: productId }), { body }),
        updateVariant: (variantId, body) => this.client.request('PATCH', route('adminCatalog_updateVariant', { variantId }), { body }),
        removeVariant: (variantId) => this.client.request('DELETE', route('adminCatalog_removeVariant', { variantId })),
        addImage: (productId, body) => this.client.request('POST', route('adminCatalog_addImage', { id: productId }), { body }),
        /**
         * Links an image to a colour — `variantId` is any variant of it — or, with
         * null, makes it a general photograph; and sets its alt text. Fields left
         * out stay as they are.
         */
        updateImage: (imageId, body) => this.client.request('PATCH', route('adminCatalog_updateImage', { imageId }), { body }),
        /** Every image id, in the order they should appear. */
        reorderImages: (productId, imageIds) => this.client.request('PATCH', route('adminCatalog_reorderImages', { id: productId }), { body: { imageIds } }),
        removeImage: (imageId) => this.client.request('DELETE', route('adminCatalog_removeImage', { imageId })),
    };
    /**
     * The team: staff accounts and the permission groups behind them.
     *
     * Owner-only on the API side — whoever can edit permissions can grant
     * themselves any of them.
     */
    staff = {
        /** Every permission, grouped by area, so the UI renders rather than hard-codes it. */
        permissions: () => this.client.request('GET', route('staff_permissions')),
        list: (params) => this.client.requestList('GET', route('staff_listStaff'), { query: query(params) }),
        get: (id, locale) => this.client.request('GET', route('staff_getStaff', { id }), {
            query: query({ locale }),
        }),
        create: (body) => this.client.request('POST', route('staff_createStaff'), { body }),
        update: (id, body) => this.client.request('PATCH', route('staff_updateStaff', { id }), { body }),
        /** An owner setting a colleague's password; other sessions are left alone. */
        setPassword: (id, password) => this.client.request('POST', route('staff_setPassword', { id }), { body: { password } }),
        /** A colleague who lost their phone sets two-step sign-in up again (ADR-116). */
        resetTwoFactor: (id) => this.client.request('POST', route('staff_resetTwoFactor', { id })),
        groups: {
            list: (params) => this.client.requestList('GET', route('staff_listGroups'), {
                query: query(params),
            }),
            create: (body) => this.client.request('POST', route('staff_createGroup'), { body }),
            update: (id, body) => this.client.request('PATCH', route('staff_updateGroup', { id }), { body }),
            /** Refused while anyone is in it, and refused outright for a seeded group. */
            remove: (id) => this.client.request('DELETE', route('staff_deleteGroup', { id })),
        },
    };
    /**
     * Payment providers, plug and play (ADR-025).
     *
     * Every call returns the whole list, so the screen re-renders from one object
     * rather than guessing what a mutation did to the rest.
     */
    /** A store's own colours, logo and type (ADR-026). */
    branding = {
        get: () => this.client.request('GET', route('settings_brandingState')),
        update: (body) => this.client.request('PATCH', route('settings_updateBranding'), { body }),
    };
    /** Support tickets — the staff half (ADR-028). */
    tickets = {
        list: (params) => this.client.requestList('GET', route('supportAdmin_list'), { query: query(params) }),
        get: (id, locale) => this.client.request('GET', route('supportAdmin_get', { id }), {
            query: query({ locale }),
        }),
        /** A reply moves it to "waiting on the customer"; an internal note changes nothing. */
        reply: (id, body, locale) => this.client.request('POST', route('supportAdmin_reply', { id }), {
            body,
            query: query({ locale }),
        }),
        update: (id, body, locale) => this.client.request('PATCH', route('supportAdmin_update', { id }), {
            body,
            query: query({ locale }),
        }),
    };
    /** Third-party services a store switches on for itself (ADR-029). */
    /** WhatsApp through Meta's own Cloud API (ADR-105): the connection, the templates, a test. */
    whatsapp = {
        overview: () => this.client.request('GET', route('whatsApp_overview')),
        /** Sends Meta the platform's templates it has not seen, then reads every verdict back. */
        submitTemplates: () => this.client.requestList('POST', route('whatsApp_submit')),
        refreshTemplates: () => this.client.requestList('POST', route('whatsApp_refresh')),
        /** The store's own templates on Meta a campaign could send (ADR-106). */
        marketingTemplates: () => this.client.requestList('GET', route('whatsApp_marketingTemplates')),
        /** Meta's own hello_world to a number. */
        test: (body) => this.client.request('POST', route('whatsApp_test'), { body }),
    };
    integrations = {
        list: () => this.client.requestList('GET', route('integrationsAdmin_list')),
        update: (key, body) => this.client.requestList('PATCH', route('integrationsAdmin_update', { key }), { body }),
    };
    /** Purchases reported to ad platforms from the server (ADR-100): the last week per platform. */
    conversions = {
        summary: () => this.client.requestList('GET', route('conversions_summary')),
    };
    /** Texting and one-time codes (ADR-030). */
    sms = {
        config: () => this.client.request('GET', route('sms_config')),
        update: (body) => this.client.request('PATCH', route('sms_update'), { body }),
    };
    /** The store's own header menu. */
    navigation = {
        get: () => this.client.request('GET', route('settings_navigation')),
        /** The whole arrangement at once — a menu is one order, not a set of rows. */
        update: (items) => this.client.request('PATCH', route('settings_updateNavigation'), {
            body: { items },
        }),
    };
    /** Keys a third party integrates with (ADR-031). */
    apiKeys = {
        list: () => this.client.requestList('GET', route('apiKeys_list')),
        /** The secret is in this response and nowhere else, ever. */
        create: (body) => this.client.request('POST', route('apiKeys_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('apiKeys_update', { id }), { body }),
        revoke: (id) => this.client.request('DELETE', route('apiKeys_revoke', { id })),
        requests: (id, params) => this.client.requestList('GET', route('apiKeys_requests', { id }), {
            query: query(params),
        }),
    };
    /** Broadcasts to customers who agreed to hear from the store (ADR-036). */
    campaigns = {
        list: (params) => this.client.requestList('GET', route('campaigns_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('campaigns_get', { id })),
        /** How many consenting customers this would reach — ask before sending. */
        /** Consent only: accounts, plus confirmed subscribers when the campaign can reach them (ADR-040). */
        audience: (body) => this.client.request('POST', route('campaigns_audience'), {
            body,
        }),
        create: (body) => this.client.request('POST', route('campaigns_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('campaigns_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('campaigns_remove', { id })),
        test: (id, to) => this.client.request('POST', route('campaigns_test', { id }), { body: { to } }),
        /** Now, or at `at` (ADR-099): a scheduled campaign waits for its time. */
        send: (id, at) => this.client.request('POST', route('campaigns_send', { id }), { body: at ? { at } : {} }),
        /** A scheduled campaign back to a draft (ADR-099). */
        unschedule: (id) => this.client.request('POST', route('campaigns_unschedule', { id })),
        cancel: (id) => this.client.request('POST', route('campaigns_cancel', { id })),
        /** While testing (ADR-113): send this subject to the rest now, rather than wait. */
        pickWinner: (id, variant) => this.client.request('POST', route('campaigns_pickWinner', { id }), { body: { variant } }),
    };
    /** Emails that send themselves (ADR-099): their numbers, and the sweep by hand. Their setting is `marketing.automations`. */
    automations = {
        stats: (days = 30) => this.client.requestList('GET', route('automations_stats'), { query: { days } }),
        run: () => this.client.request('POST', route('automations_run')),
    };
    /** The newsletter's list (ADR-099). */
    subscribers = {
        list: (params) => this.client.requestList('GET', route('adminSubscribers_list'), { query: query(params) }),
        counts: () => this.client.request('GET', route('adminSubscribers_counts')),
        exportCsv: (status) => this.client.download(route('adminSubscribers_export'), { query: status ? { status } : {} }),
        withdraw: (id) => this.client.request('POST', route('adminSubscribers_withdraw', { id })),
    };
    /** Every email and text, sent or attempted (ADR-034). */
    messages = {
        list: (params) => this.client.requestList('GET', route('messages_list'), { query: query(params) }),
        failures: () => this.client.request('GET', route('messages_failures')),
        /** Sends a failed message again, exactly as it was. */
        retry: (id) => this.client.request('POST', route('messages_retry', { id })),
    };
    /** Search maintenance (ADR-037). */
    searchIndex = {
        /** Rebuild the product index atomically. Run after a CSV import. */
        reindex: () => this.client.request('POST', route('search_reindex')),
    };
    /** The assistant (ADR-033). Every one of these returns a draft, never a save. */
    ai = {
        config: () => this.client.request('GET', route('ai_get')),
        update: (body) => this.client.request('PUT', route('ai_update'), { body }),
        test: () => this.client.request('POST', route('ai_test')),
        history: (params) => this.client.requestList('GET', route('ai_history'), { query: query(params) }),
        productCopy: (body) => this.client.request('POST', route('ai_productCopy'), { body }),
        translate: (body) => this.client.request('POST', route('ai_translate'), { body }),
        ticketReply: (id) => this.client.request('POST', route('ai_ticketReply', { id })),
        reviewTriage: (id) => this.client.request('POST', route('ai_reviewTriage', { id })),
    };
    /** Telling other systems what happened (ADR-032). */
    webhooks = {
        events: () => this.client.request('GET', route('webhooks_events')),
        list: () => this.client.requestList('GET', route('webhooks_list')),
        create: (body) => this.client.request('POST', route('webhooks_create'), { body }),
        /** The chats a Telegram bot has seen lately, so its chat id need not be hunted for (ADR-110). */
        telegramChats: (botToken) => this.client.requestList('POST', route('webhooks_telegramChats'), { body: { botToken } }),
        update: (id, body) => this.client.request('PATCH', route('webhooks_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('webhooks_remove', { id })),
        /** A real delivery of a sample body, down the same path a real event takes. */
        test: (id) => this.client.request('POST', route('webhooks_test', { id })),
        /** Sends one recorded delivery again — for after the receiver is fixed. */
        resend: (deliveryId) => this.client.request('POST', route('webhooks_resend', { deliveryId })),
        deliveries: (id, params) => this.client.requestList('GET', route('webhooks_deliveries', { id }), {
            query: query(params),
        }),
    };
    payments = {
        providers: () => this.client.requestList('GET', route('paymentsAdmin_providers')),
        update: (key, body) => this.client.requestList('PATCH', route('paymentsAdmin_update', { key }), { body }),
    };
    /**
     * The audit trail — read-only, because that is what makes it a trail.
     */
    audit = {
        list: (params) => this.client.requestList('GET', route('audit_list'), { query: query(params) }),
        /** The actions actually recorded, for the filter. */
        actions: () => this.client.request('GET', route('audit_actions')),
    };
    uploads = {
        /** Returns the storage key an image field then references. */
        image: (file) => {
            const form = new FormData();
            form.append('file', file);
            return this.client.requestMultipart('POST', route('uploads_uploadImage'), form);
        },
        /** A product film, MP4 or WebM up to 50 MB (R4); returns its storage key. */
        video: (file) => {
            const form = new FormData();
            form.append('file', file);
            return this.client.requestMultipart('POST', route('uploads_uploadVideo'), form);
        },
    };
    inventory = {
        lowStock: (params) => this.client.requestList('GET', route('admin_lowStock'), {
            query: query(params),
        }),
        movements: (variantId, params) => this.client.requestList('GET', route('admin_movements', { variantId }), { query: query(params) }),
        recount: (variantId, body) => this.client.request('PATCH', route('admin_adjust', { variantId }), { body }),
        /** What each location holds of a variant (ADR-089). */
        byLocation: (variantId) => this.client.request('GET', route('stockLocations_byLocation', { variantId })),
        /** Moves stock between locations; the total does not change. */
        transfer: (variantId, body) => this.client.request('POST', route('stockLocations_transfer', { variantId }), { body }),
    };
    /** SEO health across the catalogue (ADR-095). */
    seo = {
        health: () => this.client.request('GET', route('seo_health')),
        issues: (params) => this.client.requestList('GET', route('seo_issues'), { query: query(params) }),
    };
    /** Popups (ADR-098): written by staff, shown by the storefront at the right moment. */
    popups = {
        list: (params) => this.client.requestList('GET', route('adminPopups_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('adminPopups_get', { id })),
        create: (body) => this.client.request('POST', route('adminPopups_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('adminPopups_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('adminPopups_remove', { id })),
    };
    /** Product feeds for Google, Meta, TikTok and Snapchat, checked (ADR-096). */
    feeds = {
        status: () => this.client.request('GET', route('feeds_status')),
    };
    /** Warehouses and branches (ADR-089). */
    stockLocations = {
        list: (params) => this.client.requestList('GET', route('stockLocations_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('stockLocations_get', { id })),
        create: (body) => this.client.request('POST', route('stockLocations_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('stockLocations_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('stockLocations_remove', { id })),
    };
    /**
     * Shipping zones, methods, and price bands.
     *
     * Every call returns the affected zone in full: a method's price only means
     * anything beside the zone it belongs to, so the screen re-renders from one
     * object rather than stitching responses together.
     */
    /**
     * Automatic discounts — campaigns the shopper never types a code for.
     *
     * Separate from `discounts`, which is the coupon surface: the two answer
     * different questions and share no fields worth merging.
     */
    /**
     * Product specifications — the typed facts a product carries.
     *
     * Three surfaces: the library itself, the per-category template, and the
     * values on one product.
     */
    attributes = {
        list: () => this.client.requestList('GET', route('attributes_list')),
        create: (body) => this.client.request('POST', route('attributes_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('attributes_update', { id }), { body }),
        /** Retired, not deleted: products keep the answers they gave. */
        deactivate: (id) => this.client.request('DELETE', route('attributes_deactivate', { id })),
        /**
         * What a category asks its products, including what it inherits;
         * `own: true` returns only what it declares itself — the template editor's list.
         */
        forCategory: (categoryId, params) => this.client.requestList('GET', route('attributes_categoryAttributes', { id: categoryId }), { query: query(params) }),
        setForCategory: (categoryId, attributes) => this.client.requestList('PUT', route('attributes_setCategoryAttributes', { id: categoryId }), { body: { attributes } }),
        forProduct: (productId) => this.client.requestList('GET', route('attributes_productAttributes', { id: productId })),
        setForProduct: (productId, values) => this.client.requestList('PUT', route('attributes_setProductAttributes', { id: productId }), { body: { values } }),
    };
    /**
     * Customer-entered inputs (S11): the question library, and which products or
     * categories offer each one.
     */
    productFields = {
        list: () => this.client.requestList('GET', route('productFieldsAdmin_list')),
        forProduct: (productId) => this.client.requestList('GET', route('productFieldsAdmin_forProduct', { id: productId })),
        setForProduct: (productId, fields) => this.client.requestList('PUT', route('productFieldsAdmin_setForProduct', { id: productId }), { body: { fields } }),
        forCategory: (categoryId) => this.client.requestList('GET', route('productFieldsAdmin_forCategory', { id: categoryId })),
        setForCategory: (categoryId, fields) => this.client.requestList('PUT', route('productFieldsAdmin_setForCategory', { id: categoryId }), { body: { fields } }),
    };
    promotions = {
        list: () => this.client.requestList('GET', route('promotionsAdmin_list')),
        create: (body) => this.client.request('POST', route('promotionsAdmin_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('promotionsAdmin_update', { id }), { body }),
        /** Switched off, never deleted: orders record what discounted them. */
        deactivate: (id) => this.client.request('DELETE', route('promotionsAdmin_deactivate', { id })),
        /** What an offer did, over every order it applied to (ADR-063). */
        usage: (id) => this.client.request('GET', route('offerUsage_offerUsage', { id })),
        orders: (id, params) => this.client.requestList('GET', route('offerUsage_offerOrders', { id }), { query: query(params) }),
    };
    shipping = {
        zones: () => this.client.requestList('GET', route('shippingAdmin_zones')),
        /** Shipping providers the store has connected (ADR-128) — what a delivery method may ship with. */
        providers: () => this.client.requestList('GET', route('shippingProviders_list')),
        /** A connected provider's couriers the store's account can use, cheapest first (ADR-128). */
        providerServices: (key) => this.client.requestList('GET', route('shippingProviders_services', { key })),
        createZone: (body) => this.client.request('POST', route('shippingAdmin_createZone'), { body }),
        updateZone: (id, body) => this.client.request('PATCH', route('shippingAdmin_updateZone', { id }), {
            body,
        }),
        addMember: (zoneId, body) => this.client.request('POST', route('shippingAdmin_addMember', { id: zoneId }), { body }),
        removeMember: (memberId) => this.client.request('DELETE', route('shippingAdmin_removeMember', { memberId })),
        createMethod: (zoneId, body) => this.client.request('POST', route('shippingAdmin_createMethod', { id: zoneId }), { body }),
        updateMethod: (methodId, body) => this.client.request('PATCH', route('shippingAdmin_updateMethod', { methodId }), { body }),
        /** Deactivates: past orders name the method they were delivered by. */
        deactivateMethod: (methodId) => this.client.request('DELETE', route('shippingAdmin_deactivateMethod', { methodId })),
    };
    /** What a digital product delivers (ADR-077): a download's files, a code's pools. */
    digital = {
        files: (productId) => this.client.requestList('GET', route('digitalAdmin_listFiles', { id: productId })),
        /** PDF, ZIP (EPUB and Office too), MP3, MP4 or an image, up to 100 MB — private. */
        addFile: (productId, file) => {
            const form = new FormData();
            form.append('file', file);
            return this.client.requestMultipart('POST', route('digitalAdmin_addFile', { id: productId }), form);
        },
        renameFile: (id, name) => this.client.request('PATCH', route('digitalAdmin_renameFile', { id }), { body: { name } }),
        removeFile: (id) => this.client.request('DELETE', route('digitalAdmin_removeFile', { id })),
        /** One pool per variant, counted. */
        pools: (productId) => this.client.requestList('GET', route('digitalAdmin_pools', { id: productId })),
        codes: (variantId, params) => this.client.requestList('GET', route('digitalAdmin_listCodes', { id: variantId }), { query: query(params) }),
        /** One code a line; repeats counted, not added. */
        addCodes: (variantId, text) => this.client.request('POST', route('digitalAdmin_addCodes', { id: variantId }), { body: { text } }),
        removeCode: (id) => this.client.request('DELETE', route('digitalAdmin_removeCode', { id })),
    };
    /** Store credit (ADR-081): a customer's balance, its ledger, and what the store owes. */
    wallet = {
        get: (customerId) => this.client.request('GET', route('wallet_balance', { id: customerId })),
        entries: (customerId, params) => this.client.requestList('GET', route('wallet_entries', { id: customerId }), { query: query(params) }),
        /** Signed halalas, with a reason; never below zero. */
        adjust: (customerId, body) => this.client.request('POST', route('wallet_adjust', { id: customerId }), { body }),
        summary: () => this.client.request('GET', route('wallet_summary')),
    };
    /** A simple CRM (ADR-104): each customer's timeline, and follow-ups — what must happen next, by whom and when. */
    crm = {
        /** Everything with a customer, newest first, paged by an opaque cursor. */
        timeline: (customerId, params) => this.client.requestList('GET', route('crm_timeline', { id: customerId }), { query: query(params) }),
        followUps: (params) => this.client.requestList('GET', route('crm_list'), { query: query(params) }),
        followUp: (id) => this.client.request('GET', route('crm_get', { id })),
        create: (body) => this.client.request('POST', route('crm_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('crm_update', { id }), { body }),
        done: (id) => this.client.request('POST', route('crm_complete', { id })),
        reopen: (id) => this.client.request('POST', route('crm_reopen', { id })),
        remove: (id) => this.client.request('DELETE', route('crm_remove', { id })),
        /** Who a follow-up can be given to — names only, open to staff. */
        assignees: (params) => this.client.requestList('GET', route('crm_assignees'), { query: query(params) }),
    };
    /** Customer groups by rules (ADR-085): kept members, refreshed nightly and on request. */
    customerGroups = {
        list: (params) => this.client.requestList('GET', route('customerGroups_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('customerGroups_get', { id })),
        create: (body) => this.client.request('POST', route('customerGroups_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('customerGroups_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('customerGroups_remove', { id })),
        refresh: (id) => this.client.request('POST', route('customerGroups_refresh', { id })),
        /** Who the rules would take in; nothing saved. */
        preview: (rules) => this.client.request('POST', route('customerGroups_preview'), { body: { rules } }),
        ofCustomer: (customerId) => this.client.requestList('GET', route('customerGroups_groupsOf', { id: customerId })),
    };
    /** Affiliates (ADR-084): marketers, their commissions, and payouts recorded by hand. */
    affiliates = {
        list: (params) => this.client.requestList('GET', route('affiliates_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('affiliates_get', { id })),
        create: (body) => this.client.request('POST', route('affiliates_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('affiliates_update', { id }), { body }),
        commissions: (id, params) => this.client.requestList('GET', route('affiliates_commissions', { id }), { query: query(params) }),
        payouts: (id) => this.client.requestList('GET', route('affiliates_payouts', { id })),
        /** Everything approved, paid by hand. */
        pay: (id, body) => this.client.request('POST', route('affiliates_pay', { id }), { body }),
    };
    /** Loyalty points (ADR-083): a customer's points, their ledger, and what is out there. */
    loyalty = {
        get: (customerId) => this.client.request('GET', route('loyalty_view', { id: customerId })),
        entries: (customerId, params) => this.client.requestList('GET', route('loyalty_entries', { id: customerId }), { query: query(params) }),
        /** Signed points, with a reason; never below zero. */
        adjust: (customerId, body) => this.client.request('POST', route('loyalty_adjust', { id: customerId }), { body }),
        summary: () => this.client.request('GET', route('loyalty_summary')),
    };
    /** Gift cards (ADR-082): issued, voided and resent by staff; redeemed into store credit. */
    giftCards = {
        list: (params) => this.client.requestList('GET', route('giftCards_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('giftCards_get', { id })),
        /** Their codes come back whole, the one time they do. */
        issue: (body) => this.client.request('POST', route('giftCards_issue'), { body }),
        void: (id, reason) => this.client.request('POST', route('giftCards_void', { id }), { body: { reason } }),
        resend: (id) => this.client.request('POST', route('giftCards_resend', { id })),
        summary: () => this.client.request('GET', route('giftCards_summary')),
    };
    /** The store's blog (ADR-080): drafts, scheduled and published posts. */
    blog = {
        list: (params) => this.client.requestList('GET', route('blog_list'), { query: query(params) }),
        get: (id, locale) => this.client.request('GET', route('blog_get', { id }), { query: query({ locale }) }),
        create: (body, locale) => this.client.request('POST', route('blog_create'), { body, query: query({ locale }) }),
        update: (id, body, locale) => this.client.request('PATCH', route('blog_update', { id }), { body, query: query({ locale }) }),
        remove: (id) => this.client.request('DELETE', route('blog_remove', { id })),
    };
    /** Old links that keep working (ADR-073). */
    redirects = {
        list: (params) => this.client.requestList('GET', route('redirects_list'), { query: query(params) }),
        create: (body) => this.client.request('POST', route('redirects_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('redirects_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('redirects_remove', { id })),
        /** One pair a line, up to 500; each added, updated, or refused with its reason. */
        bulk: (body) => this.client.request('POST', route('redirects_bulk'), { body }),
    };
    /** Size charts (ADR-072): written once, hung on categories and products. */
    sizeCharts = {
        list: (params) => this.client.requestList('GET', route('sizeCharts_list'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('sizeCharts_get', { id })),
        create: (body) => this.client.request('POST', route('sizeCharts_create'), { body }),
        update: (id, body) => this.client.request('PATCH', route('sizeCharts_update', { id }), { body }),
        remove: (id) => this.client.request('DELETE', route('sizeCharts_remove', { id })),
    };
    invoices = {
        /** The tax invoice for an order; issued on first request (S2). */
        forOrder: (orderId) => this.client.download(route('invoicing_adminInvoice', { id: orderId })),
        /** Every invoice and credit note, newest first (ADR-067). */
        list: (params) => this.client.requestList('GET', route('invoicing_list'), {
            query: query(params),
        }),
        /** What the filter adds up to — invoiced, credited and net, with the VAT in each. */
        summary: (params) => this.client.request('GET', route('invoicing_summary'), {
            query: query(params),
        }),
        /** The filtered documents as a CSV, built on the server (audited). */
        export: (params) => this.client.download(route('invoicing_export'), {
            query: query(params),
        }),
        /** One invoice or credit note, as issued. */
        document: (id) => this.client.download(route('invoicing_document', { id })),
    };
    /** The shell's own reads: whose admin this is, and what is waiting on the team. */
    shell = {
        workspace: () => this.client.request('GET', route('admin_workspace')),
        /** Counts per area the person may read; an area they may not read is absent. */
        attention: () => this.client.request('GET', route('admin_attention')),
    };
    /** The signed-in person's own (ADR-109): their overview's sections and their summary by email. */
    me = {
        preferences: () => this.client.request('GET', route('preferences_get')),
        updatePreferences: (body) => this.client.request('PATCH', route('preferences_update'), { body }),
        /** Their own two-step sign-in (ADR-116). */
        twoFactor: {
            status: () => this.client.request('GET', route('twoFactor_status')),
            /** A new secret, on once `enable` gets its first code. */
            setup: () => this.client.request('POST', route('twoFactor_setup')),
            enable: (code) => this.client.request('POST', route('twoFactor_enable'), { body: { code } }),
            renewRecoveryCodes: (code) => this.client.request('POST', route('twoFactor_renew'), { body: { code } }),
            disable: (code) => this.client.request('POST', route('twoFactor_disable'), { body: { code } }),
        },
    };
    reports = {
        /** The dashboard's rolling window: the last `days` days (30 by default). */
        summary: (params) => this.client.request('GET', route('admin_summary'), {
            query: query(params),
        }),
        /** Revenue and orders per day over the last `days` days, with no gaps — the dashboard's chart. */
        salesTrend: (params) => this.client.requestList('GET', route('admin_sales'), { query: query(params) }),
        /** The ten best sellers over the last `days` days. */
        topSellers: (params) => this.client.requestList('GET', route('admin_topProducts'), { query: query(params) }),
        // ─── Over an explicit range ──────────────────────────────────────────────
        // `summary` above is the dashboard's rolling window. The rest take a range,
        // because "what happened between these two dates?" is the question anyone
        // reconciling a month actually asks. The day-based sales and top-products
        // endpoints still exist on the API; nothing needs them through the SDK.
        orders: (range, filters = {}) => this.client.request('GET', route('reports_orders'), { query: { ...range, ...filtersToQuery(filters) } }),
        /** Sales by source (ADR-097): per channel, and the top source / medium / campaign rows. */
        sources: (range, filters = {}) => this.client.request('GET', route('reports_sources'), { query: { ...range, ...filtersToQuery(filters) } }),
        /** The funnel from the store's own visits (ADR-108), against the period before. */
        funnel: (range) => this.client.request('GET', route('reports_funnel'), { query: { ...range } }),
        /** Per product: views, adds, units, revenue, conversion, stock and days of cover — sorted on the server. */
        productPerformance: (range, params) => this.client.request('GET', route('reports_productPerformance'), { query: query({ ...range, ...params }) }),
        /** Cohorts by first order month, and the repeat rate (ADR-108). */
        retention: (range) => this.client.request('GET', route('reports_retention'), { query: { ...range } }),
        /** Lifetime value of the period's new buyers, by first source and stage; stages at each month end (ADR-109). */
        customerValue: (range) => this.client.request('GET', route('reports_customerValue'), { query: { ...range } }),
        customers: (range, filters = {}) => this.client.request('GET', route('reports_customers'), {
            query: { ...range, ...filtersToQuery(filters) },
        }),
        finance: (range, filters = {}) => this.client.request('GET', route('reports_finance'), {
            query: { ...range, ...filtersToQuery(filters) },
        }),
        discounts: (range, filters = {}) => this.client.request('GET', route('reports_discounts'), {
            query: { ...range, ...filtersToQuery(filters) },
        }),
        salesByDay: (range, filters = {}) => this.client.request('GET', route('reports_salesByDay'), {
            query: { ...range, ...filtersToQuery(filters) },
        }),
        topProducts: (range, filters = {}) => this.client.request('GET', route('reports_products'), {
            query: { ...range, ...filtersToQuery(filters) },
        }),
        // ─── Reports v3 (ADR-118 – ADR-122) ──────────────────────────────────────
        /** Profit on the goods: totals, by day, product, category and brand. */
        profit: (range, filters = {}, locale) => this.client.request('GET', route('analytics_profitReport'), { query: query({ ...range, ...filtersToQuery(filters), locale }) }),
        /** What the shelves hold: at cost and at price without VAT. */
        stockValue: (locale) => this.client.request('GET', route('analytics_stockValue'), { query: query({ locale }) }),
        /** Owner only: lines sold before costs were entered take today's cost. */
        backfillCosts: () => this.client.request('POST', route('analytics_backfill')),
        geography: (range, filters = {}) => this.client.request('GET', route('analytics_geography'), { query: { ...range, ...filtersToQuery(filters) } }),
        shipping: (range, filters = {}) => this.client.request('GET', route('analytics_shipping'), { query: { ...range, ...filtersToQuery(filters) } }),
        returns: (range, filters = {}) => this.client.request('GET', route('analytics_returns'), { query: { ...range, ...filtersToQuery(filters) } }),
        carts: (range) => this.client.request('GET', route('analytics_carts'), { query: { ...range } }),
        /** Needs staff:manage beside dashboard:read. */
        staff: (range) => this.client.request('GET', route('analytics_staff'), { query: { ...range } }),
        live: () => this.client.request('GET', route('analytics_live')),
        target: () => this.client.request('GET', route('analytics_target')),
        insights: (locale) => this.client.request('GET', route('analytics_insights'), { query: query({ locale }) }),
        /** Regions, cities, coupons or carriers orders hold — searched and paged on the server. */
        filterValues: (params) => this.client.request('GET', route('analytics_filterValues'), { query: query(params) }),
        custom: (definition, range, filters = {}, locale) => this.client.request('GET', route('analytics_runCustom'), {
            query: query({ ...range, ...filtersToQuery(filters), locale, definition: JSON.stringify(definition) }),
        }),
        exportCustom: (format, definition, range, filters = {}, locale) => this.client.download(route('analytics_exportCustom', { format }), {
            query: query({ ...range, ...filtersToQuery(filters), locale, definition: JSON.stringify(definition) }),
        }),
        views: {
            list: (report) => this.client.request('GET', route('reportViews_listViews'), { query: query({ report }) }),
            create: (body) => this.client.request('POST', route('reportViews_createView'), { body }),
            update: (id, body) => this.client.request('PATCH', route('reportViews_updateView', { id }), { body }),
            remove: (id) => this.client.request('DELETE', route('reportViews_removeView', { id })),
        },
        /** The CSV for a report, already generated server-side over the whole range — filtered where the report takes filters. */
        exportCsv: (key, range, filters = {}) => this.client.download(route('reports_export', { key }), { query: { ...range, ...filtersToQuery(filters) } }),
        /** The same table as the CSV, as a styled workbook with provenance. */
        exportXlsx: (key, range, filters = {}) => this.client.download(route('reports_exportXlsx', { key }), { query: { ...range, ...filtersToQuery(filters) } }),
        /** A circulate-able document: logo, period, author, page numbers. */
        exportPdf: (key, range, filters = {}) => this.client.download(route('reports_exportPdf', { key }), { query: { ...range, ...filtersToQuery(filters) } }),
    };
    returns = {
        list: (params) => this.client.requestList('GET', route('returns_adminReturns'), { query: query(params) }),
        get: (id) => this.client.request('GET', route('returns_adminReturn', { id })),
        approve: (id, body) => this.client.request('PATCH', route('returns_approve', { id }), { body }),
        reject: (id, body) => this.client.request('PATCH', route('returns_reject', { id }), { body }),
        /** What happened to the goods, and — only with RESTOCKED — whether they go back on sale. */
        receive: (id, body) => this.client.request('PATCH', route('returns_receive', { id }), { body }),
        /** A cash order's refund needs `settlement`: how the money went back (ADR-061). */
        refund: (id, body) => this.client.request('PATCH', route('returns_refund', { id }), { body }),
        /** A courier to collect the order's approved returns, through its shipping provider (ADR-130). */
        bookPickup: (id) => this.client.request('POST', route('returns_bookPickup', { id })),
    };
    reviews = {
        list: (params) => this.client.requestList('GET', route('reviews_queue'), { query: query(params) }),
        moderate: (id, body) => this.client.request('PATCH', route('reviews_moderate', { id }), { body }),
        /** The store's public answer (R8): a second write replaces it. */
        reply: (id, body) => this.client.request('PUT', route('reviews_reply', { id }), { body: { body } }),
        removeReply: (id) => this.client.request('DELETE', route('reviews_removeReply', { id })),
    };
    /** The questions queue (R20, ADR-047): answering publishes a question; nothing unanswered is public. */
    questions = {
        list: (params) => this.client.requestList('GET', route('questions_queue'), { query: query(params) }),
        /** Writes or rewrites the store's answer, which publishes the question. */
        answer: (id, body) => this.client.request('PUT', route('questions_answer', { id }), { body: { body } }),
        /** Publish an answered question again, or hide one. */
        moderate: (id, status) => this.client.request('PATCH', route('questions_moderate', { id }), { body: { status } }),
    };
    /**
     * The bulk import pipeline (ADR-014).
     *
     * Four steps, deliberately separate: upload, map, dry-run, import. The
     * progress stream is server-sent events and is read directly rather than
     * through this client — `EventSource` cannot carry an Authorization header.
     */
    ingestion = {
        /** The fields a column maps to — products by default, or customers (ADR-070). */
        fields: (target) => this.client.request('GET', route('ingestion_fields'), { query: query({ target }) }),
        profiles: (params) => this.client.requestList('GET', route('ingestion_profiles'), {
            query: query(params),
        }),
        createProfile: (body) => this.client.request('POST', route('ingestion_createProfile'), { body }),
        deleteProfile: (id) => this.client.request('DELETE', route('ingestion_deleteProfile', { id })),
        uploads: (params) => this.client.requestList('GET', route('ingestion_uploads'), {
            query: query(params),
        }),
        batch: (id) => this.client.request('GET', route('ingestion_batch', { id })),
        /** Step 1. Multipart, so it bypasses the JSON body path. */
        upload: (file, target = 'PRODUCTS') => {
            const form = new FormData();
            form.append('file', file);
            form.append('target', target);
            return this.client.requestMultipart('POST', route('ingestion_upload'), form);
        },
        /** Step 2 — writes nothing; reports which rows would fail. */
        validate: (id, body) => this.client.request('POST', route('ingestion_validate', { id }), { body }),
        /** Step 3 — the only step that touches the catalogue. */
        run: (id) => this.client.request('POST', route('ingestion_run', { id })),
        cancel: (id) => this.client.request('POST', route('ingestion_cancel', { id })),
        /** Every rejected row with its reason, as a CSV to fix and upload again. */
        errorReport: (id) => this.client.download(route('ingestion_errors', { id })),
    };
    /**
     * The wording of transactional email.
     *
     * `list` returns every template the platform sends, whether or not the store
     * has edited it — the shipped wording is what an unedited one shows.
     */
    emailTemplates = {
        list: () => this.client.request('GET', route('emailTemplates_list')),
        get: (key) => this.client.request('GET', route('emailTemplates_get', { key })),
        update: (key, body) => this.client.request('PUT', route('emailTemplates_update', { key }), { body }),
        /** Discards the store's wording; the shipped one applies again. */
        reset: (key) => this.client.request('POST', route('emailTemplates_reset', { key })),
        preview: (key) => this.client.request('GET', route('emailTemplates_preview', { key })),
        sendTest: (key, to) => this.client.request('POST', route('emailTemplates_sendTest', { key }), { body: { to } }),
    };
    settings = {
        all: () => this.client.request('GET', route('settings_all')),
        update: (key, value) => this.client.request('PATCH', route('settings_update', { key }), { body: { value } }),
        /**
         * Keeps a home page draft for 30 minutes behind a link (ADR-056), checked
         * exactly as saving `home.layout` would check it. Nothing is published.
         */
        homePreview: (blocks) => this.client.request('POST', route('settings_createHomePreview'), { body: { blocks } }),
        /**
         * What the deployed theme says about its home-page sections (ADR-057),
         * read from the storefront and cached for five minutes. `refresh` asks the
         * storefront now — the editor's "try again".
         */
        homeSections: (refresh) => this.client.request('GET', route('settings_homeSections'), {
            query: refresh ? { refresh: 'true' } : undefined,
        }),
    };
}
//# sourceMappingURL=admin.js.map